
import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Leaf, ArrowUpRight, ShieldCheck } from 'lucide-react';
import { BUSINESS_INFO } from '../constants';

const Footer: React.FC = () => {
  return (
    <footer className="relative bg-slate-900 text-slate-300 overflow-hidden">
      {/* Subtle top animated border */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-teal-500/50 to-transparent animate-pulse"></div>
      
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-700">
            <div className="flex items-center space-x-3 text-white">
              <div className="bg-teal-500/10 p-2 rounded-lg">
                <Leaf className="h-7 w-7 text-teal-400 animate-pulse-soft" />
              </div>
              <span className="text-xl font-bold tracking-tight">Sangeeta Wellness</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              We focus on providing products and services that support daily wellness routines and mindful living with integrity and transparency.
            </p>
            <div className="flex items-center space-x-2 text-xs font-bold text-teal-400 uppercase tracking-widest bg-teal-500/5 border border-teal-500/10 px-3 py-1.5 rounded-full inline-flex">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Verified Business</span>
            </div>
          </div>

          {/* Navigation */}
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-700 delay-100">
            <h3 className="text-white font-bold text-sm uppercase tracking-widest">Navigation</h3>
            <ul className="space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Contact Us', path: '/contact' }
              ].map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="group flex items-center text-sm hover:text-teal-400 transition-all duration-300"
                  >
                    <span className="w-0 group-hover:w-4 h-[1px] bg-teal-400 mr-0 group-hover:mr-2 transition-all duration-300"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-700 delay-200">
            <h3 className="text-white font-bold text-sm uppercase tracking-widest">Legal</h3>
            <ul className="space-y-3">
              {[
                { name: 'Privacy Policy', path: '/privacy-policy' },
                { name: 'Terms & Conditions', path: '/terms-conditions' },
                { name: 'Disclaimer', path: '/disclaimer' },
                { name: 'Refund Policy', path: '/refund-policy' }
              ].map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="group flex items-center text-sm hover:text-teal-400 transition-all duration-300"
                  >
                    <span className="w-0 group-hover:w-4 h-[1px] bg-teal-400 mr-0 group-hover:mr-2 transition-all duration-300"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Details */}
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-700 delay-300">
            <h3 className="text-white font-bold text-sm uppercase tracking-widest">Get in Touch</h3>
            <div className="space-y-4">
              <a 
                href={`mailto:${BUSINESS_INFO.email}`}
                className="group flex items-center space-x-3 text-sm hover:text-white transition-colors"
              >
                <div className="bg-slate-800 p-2 rounded-lg group-hover:bg-teal-500/20 group-hover:text-teal-400 transition-all duration-300">
                  <Mail className="h-4 w-4" />
                </div>
                <span className="break-all">{BUSINESS_INFO.email}</span>
              </a>
              <a 
                href={`tel:${BUSINESS_INFO.phone}`}
                className="group flex items-center space-x-3 text-sm hover:text-white transition-colors"
              >
                <div className="bg-slate-800 p-2 rounded-lg group-hover:bg-teal-500/20 group-hover:text-teal-400 transition-all duration-300">
                  <Phone className="h-4 w-4" />
                </div>
                <span>{BUSINESS_INFO.phone}</span>
              </a>
              <div className="flex items-start space-x-3 text-sm">
                <div className="bg-slate-800 p-2 rounded-lg shrink-0">
                  <MapPin className="h-4 w-4" />
                </div>
                <span className="leading-relaxed">
                  {BUSINESS_INFO.address.line1}, {BUSINESS_INFO.address.city}, {BUSINESS_INFO.address.state} {BUSINESS_INFO.address.zip}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col items-center text-center space-y-4">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} {BUSINESS_INFO.name}. All rights reserved. | Registration: <span className="text-slate-400 font-mono">{BUSINESS_INFO.taxId}</span>
          </p>
          <div className="max-w-2xl text-[10px] text-slate-600 leading-relaxed uppercase tracking-tighter">
            Disclaimer: Our offerings are intended to complement general wellness routines and should not be considered a substitute for professional medical guidance. 
            Information on this site is not intended to diagnose, treat, or cure any condition.
          </div>
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="mt-4 p-3 rounded-full bg-slate-800 hover:bg-teal-600 hover:text-white transition-all duration-300 group"
          >
            <ArrowUpRight className="h-5 w-5 rotate-[-45deg] group-hover:rotate-0 transition-transform duration-500" />
          </button>
        </div>
      </div>

      {/* Background Decorative Element */}
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-teal-500/5 blur-[100px] -z-10"></div>
    </footer>
  );
};

export default Footer;
